import VConsole from 'vconsole'

/** localStorage / URL 共用标识：'1' 开启，'0' 关闭 */
export const VCONSOLE_FLAG_KEY = 'jellytech_vconsole'
export const VCONSOLE_QUERY_KEY = 'vconsole'

export type VConsoleFlag = '1' | '0'
export type VConsoleConfigSource = 'url' | 'storage' | 'env' | 'auto' | 'disabled'

export interface JellytechVConsoleControl {
  /** 当前是否已启用 */
  enabled: boolean
  /** 配置来源 */
  source: VConsoleConfigSource
  /** 打开（写入标识并初始化） */
  open: () => void
  /** 关闭（写入标识并销毁） */
  close: () => void
  /** 切换 */
  toggle: () => void
  /** 读取 localStorage 标识 */
  getFlag: () => VConsoleFlag | null
  /** 写入 localStorage 标识（'1' | '0'，传 null 清除） */
  setFlag: (flag: VConsoleFlag | null) => void
}

let instance: VConsole | null = null
let configSource: VConsoleConfigSource = 'disabled'

declare global {
  interface Window {
    __JELLYTECH_VCONSOLE__?: JellytechVConsoleControl
  }
}

function hasRnBridge(): boolean {
  return !!(window as unknown as { RNBridge?: { invokeNative?: unknown } }).RNBridge
    ?.invokeNative
}

function readStorageFlag(): VConsoleFlag | null {
  if (typeof localStorage === 'undefined') return null
  const value = localStorage.getItem(VCONSOLE_FLAG_KEY)
  return value === '1' || value === '0' ? value : null
}

export function getVConsoleFlag(): VConsoleFlag | null {
  return readStorageFlag()
}

export function setVConsoleFlag(flag: VConsoleFlag | null): void {
  if (typeof localStorage === 'undefined') return
  if (flag == null) {
    localStorage.removeItem(VCONSOLE_FLAG_KEY)
    return
  }
  localStorage.setItem(VCONSOLE_FLAG_KEY, flag)
}

function syncFlagFromUrl(): VConsoleFlag | null {
  if (typeof window === 'undefined') return null
  const value = new URLSearchParams(window.location.search).get(VCONSOLE_QUERY_KEY)
  if (value === '1' || value === '0') {
    setVConsoleFlag(value)
    return value
  }
  return null
}

function resolveAutoEnabled(): boolean {
  if (hasRnBridge()) return true

  const host = window.location.hostname
  if (host.includes('app-test.') || host.includes('localhost')) return true

  return import.meta.env.MODE !== 'prod'
}

/** 是否启用 vConsole（需在 main 最早调用 initVConsole 之前判断） */
export function isVConsoleEnabled(): boolean {
  const urlFlag = syncFlagFromUrl()
  if (urlFlag === '1') {
    configSource = 'url'
    return true
  }
  if (urlFlag === '0') {
    configSource = 'url'
    return false
  }

  const storageFlag = readStorageFlag()
  if (storageFlag === '1') {
    configSource = 'storage'
    return true
  }
  if (storageFlag === '0') {
    configSource = 'storage'
    return false
  }

  const envFlag = import.meta.env.VITE_ENABLE_VCONSOLE
  if (envFlag === 'true') {
    configSource = 'env'
    return true
  }
  if (envFlag === 'false') {
    configSource = 'env'
    return false
  }

  if (typeof window !== 'undefined' && resolveAutoEnabled()) {
    configSource = 'auto'
    return true
  }

  configSource = 'disabled'
  return false
}

export function destroyVConsole(): void {
  if (instance) {
    instance.destroy()
    instance = null
  }
}

export function setVConsoleEnabled(enabled: boolean, persist = true): VConsole | null {
  if (persist) {
    setVConsoleFlag(enabled ? '1' : '0')
  }
  if (enabled) {
    return initVConsole(true)
  }
  destroyVConsole()
  publishVConsoleControl()
  return null
}

function publishVConsoleControl(): void {
  if (typeof window === 'undefined') return
  window.__JELLYTECH_VCONSOLE__ = {
    enabled: instance != null,
    source: configSource,
    open: () => setVConsoleEnabled(true),
    close: () => setVConsoleEnabled(false),
    toggle: () => setVConsoleEnabled(!instance),
    getFlag: getVConsoleFlag,
    setFlag: setVConsoleFlag,
  }
}

/**
 * 应用启动时尽早调用，才能捕获 bootstrap、鉴权、接口等后续 console 输出。
 */
export function initVConsole(force = false): VConsole | null {
  if (instance) return instance
  if (!force && !isVConsoleEnabled()) {
    console.log('[vconsole] disabled', {
      mode: import.meta.env.MODE,
      viteFlag: import.meta.env.VITE_ENABLE_VCONSOLE,
      storageFlag: getVConsoleFlag(),
      rnBridge: typeof window !== 'undefined' ? hasRnBridge() : false,
      hint: `URL ?${VCONSOLE_QUERY_KEY}=1|0、localStorage ${VCONSOLE_FLAG_KEY}、或 VITE_ENABLE_VCONSOLE`,
    })
    publishVConsoleControl()
    return null
  }

  instance = new VConsole({ theme: 'dark' })
  console.log('[vconsole] initialized', {
    mode: import.meta.env.MODE,
    source: configSource,
    href: window.location.href,
    rnBridge: hasRnBridge(),
    control: 'window.__JELLYTECH_VCONSOLE__',
  })
  publishVConsoleControl()
  return instance
}

export function getVConsoleInstance(): VConsole | null {
  return instance
}

export function getVConsoleConfigSource(): VConsoleConfigSource {
  return configSource
}
